import { logEvent, setUserId, setUserProperties } from "firebase/analytics";
import { analytics } from "../config/firebaseConfig";
import { useAuthStore } from "../stores/useAuthStore";

/**
 * Sync logged-in user profile with Firebase / Google Analytics
 */
export const setUserAnalyticsContext = (profile: any) => {
  if (!analytics) return;
  try {
    if (profile && profile.uid) {
      setUserId(analytics, profile.uid);
      setUserProperties(analytics, {
        user_email: profile.email || "",
        user_name: profile.displayName || "",
        is_logged_in: "true",
      });
    } else {
      setUserId(analytics, "");
      setUserProperties(analytics, {
        user_email: "",
        user_name: "",
        is_logged_in: "false",
      });
    }
  } catch (error) {
    console.warn("Error setting user analytics context:", error);
  }
};

/**
 * Log a Call-To-Action (CTA) custom event to Google Analytics
 */
export const logCTAEvent = (
  eventName: string,
  params: Record<string, any> = {},
) => {
  const { profile } = useAuthStore.getState() as any;
  const isLoggedIn = profile && profile.uid;

  const userPayload = isLoggedIn
    ? {
        user_id: profile.uid,
        user_email: profile.email || "",
        user_name: profile.displayName || "",
        user_type: "authenticated",
        is_logged_in: true,
      }
    : {
        user_id: "guest",
        user_type: "guest",
        is_logged_in: false,
      };

  const isDev = process.env.NODE_ENV === "development";

  const fullPayload = {
    ...userPayload,
    ...params,
    ...(isDev ? { debug_mode: true } : {}),
  };

  console.log(`[Analytics Event] ${eventName}:`, fullPayload);

  if (analytics) {
    try {
      logEvent(analytics, eventName, fullPayload);
    } catch (error) {
      console.warn(`Error logging analytics event ${eventName}:`, error);
    }
  }
};

/**
 * Helper to log movie item specific CTA events
 */
export const logMovieCTA = (
  ctaName: string,
  movie: any,
  additionalParams: Record<string, any> = {},
) => {
  if (!movie) return;
  const movieParams = {
    item_id: movie.id || movie.movieId,
    item_name: movie.title || movie.name || movie.original_title || "",
    vote_average: movie.vote_average ?? 0,
    ...additionalParams,
  };
  logCTAEvent(ctaName, movieParams);
};

/**
 * Enable GA4 debug_mode globally via gtag if available
 */
export const enableAnalyticsDebugMode = () => {
  if (
    typeof window !== "undefined" &&
    (window as any).gtag &&
    process.env.REACT_APP_MEASUREMENT_ID
  ) {
    (window as any).gtag("config", process.env.REACT_APP_MEASUREMENT_ID, {
      debug_mode: true,
    });
  }
};
